import { isDefined } from "@clipboard-health/util-ts";
import { useRequiresBreakPolicyAcknowledgement } from "@src/appV2/redesign/Shift/Booking/useRequiresBreakPolicyAcknowledgement";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "@src/appV2/redesign/Shift/constants";
import { useShowWorkplaceRulesReminder } from "@src/appV2/redesign/WorkerShifts/hooks/useShowWorkplaceRulesReminder";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { useGetShiftState } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { differenceInMinutes, parseISO } from "date-fns";

import { useGetWorkerShift } from "../../api/useGetWorkerShift";
import { useGetPendingFullShiftMissedPunchRequest } from "../Timekeeping/api/useGetPendingFullShiftMissedPunchRequest";

export function useGetShiftDetailsData(params: { shiftId: string }) {
  const { shiftId } = params;

  const isShiftEndpointEnabled = isDefined(shiftId) && shiftId.length > 0;
  const {
    data: shiftData,
    isLoading: isShiftLoading,
    isFetching: isShiftFetching,
    error: shiftError,
    refetch: refetchShift,
  } = useGetWorkerShift(shiftId ?? "", {
    enabled: isShiftEndpointEnabled,
  });

  const shift = shiftData?.response;
  const workplaceId = shift?.facilityId;

  const {
    data: shiftState,
    error: shiftStateError,
    isLoading: isShiftStateLoading,
    refetch: refetchShiftState,
  } = useGetShiftState(
    { shiftStateDependencies: { _id: shiftId } },
    {
      enabled: isShiftEndpointEnabled,
    }
  );

  const isMissedPunchRequestEnabledV2 = shiftState?.metadata.isMissedPunchRequestEnabledV2 ?? false;

  const { data: pendingMissedPunchRequestData, refetch: refetchPendingMissedPunchRequest } =
    useGetPendingFullShiftMissedPunchRequest(shiftId, {
      enabled: isShiftEndpointEnabled && isMissedPunchRequestEnabledV2,
    });

  const pendingMissedPunchRequest = pendingMissedPunchRequestData?.data[0];
  const timeRequestedInMinutes = pendingMissedPunchRequest?.attributes?.timeRequestedInMinutes;
  const hourlyPayRateInDollars = shift?.finalPay ?? 0;
  const pendingApprovalAmountInDollars =
    isDefined(timeRequestedInMinutes) && timeRequestedInMinutes > 0 && hourlyPayRateInDollars > 0
      ? (timeRequestedInMinutes / 60) * hourlyPayRateInDollars
      : undefined;

  const doesFacilityRequireMandatoryBreak = shift?.facility.requiresLunchBreak ?? false;

  const isBreakPolicyAcknowledgementRequired =
    isDefined(shift) &&
    doesFacilityRequireMandatoryBreak &&
    differenceInMinutes(parseISO(shift.end), parseISO(shift.start)) >
      MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS * 60;

  const {
    requiresBreakPolicyAcknowledgement,
    isLoading: isPolicyAcknowledgementLoading,
    breakPolicyNoteId,
  } = useRequiresBreakPolicyAcknowledgement({
    action: NoteAcknowledgementAction.CLOCK_IN,
    workplaceId: workplaceId ?? "",
    enabled: isDefined(workplaceId) && isBreakPolicyAcknowledgementRequired,
  });

  const {
    shouldShowWorkplaceRulesReminder,
    workplaceRules,
    isLoading: isShowWorkplaceRulesReminderLoading,
  } = useShowWorkplaceRulesReminder({ workplaceId });

  return {
    shift,
    shiftState,
    // FIXME: Automatically disabled during ESLint config update
    shiftError: shiftError || shiftStateError,
    breakPolicyNoteId,
    requiresBreakPolicyAcknowledgement,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isLoading:
      isShiftLoading ||
      isPolicyAcknowledgementLoading ||
      isShiftStateLoading ||
      isShowWorkplaceRulesReminderLoading,
    isShiftDataFetching: isShiftFetching,
    pendingApprovalAmountInDollars,
    refetchShift,
    refetchShiftState,
    refetchPendingMissedPunchRequest,
  };
}
